/*
 *  Module for working with a select control's options (currently implemented for a bootstrap selectpicker)
 *  https://developer.snapappointments.com/bootstrap-select/
 */
rs.module("common/select-helper",

    // dependencies
    [],

    function () {

        // Get the selectpicker to work with
        function getHelper(selectPicker) {

            // Set the selectpicker's selected option value
            function setValue(value) {

                selectPicker.selectpicker('val', value);

                return this;
            }

            // Set the selectpicker's selected index
            function selectIndex(index) {

                selectPicker.prop('selectedIndex', index);

                selectPicker.selectpicker('render');

                return this;
            }

            // Get the selectpicker's options as an array
            function getOptions() {

                return Array.apply(null, selectPicker[0].options);
            }

            // Clear the selectpicker's options
            function clearOptions() {

                // Clear the options
                selectPicker.empty();

                // Refresh the select picker's options for display
                selectPicker.selectpicker('refresh');

                return this;
            }

            // Set the selectpicker's disabled property accordingly
            function setDisabled(disabled) {

                // toggle disabled on underlying select 
                selectPicker.prop("disabled", disabled);

                // toggle disabled class on container select-picker div (this is to match select-picker's built-in disable behavior)
                selectPicker.closest(".bootstrap-select").toggleClass("disabled", disabled);

                // toggle disabled on button that shows/hides select picker dropdown
                var toggleButton = selectPicker.next("button.dropdown-toggle");

                toggleButton.prop("disabled", disabled);
                toggleButton.toggleClass("disabled", disabled);
                toggleButton.attr("aria-disabled", disabled);

                if (disabled) {
                    toggleButton.attr("tabindex", "-1");
                }
                else {
                    toggleButton.removeAttr("tabindex");
                }

                return this;
            }

            // Prepend a blank option to the selectpicker's current options
            function addEmptyOption() {

                if (selectPicker[0].options.length == 0 ||
                    selectPicker[0].options[0].value != '') {
                    prependOptions([{ Value: '', Text: '' }]);
                }

                return this;

            }

            // Remove the blank option from the seleckpicker's current options
            function removeEmptyOption() {

                if (selectPicker[0].options.length > 0 &&
                    selectPicker[0].options[0].value == '') {

                    // remove blank option
                    selectPicker[0].options[0].remove();

                    // Refresh the select picker's options for display
                    selectPicker.selectpicker('refresh');

                    // Trigger the selectpicker's loaded event immediately
                    selectPicker.trigger('loaded.bs.select');

                    // Size the container to fit the new width of the select
                    adjustContainerWidth();
                }

                return this;
            }

            // Set the multiselect capability of the select list
            function setMultiSelect(multiSelect) {
                if (multiSelect) {
                    selectPicker.attr("multiple", "multiple");
                }
                else {
                    selectPicker.removeAttr("multiple");
                }

                // unfortunately multiple is only re-loaded in selectpicker's "init", so destroy and rerender
                selectPicker.selectpicker('destroy');
                selectPicker.selectpicker('render');
            }

            // Prepend the supplied list of options to the selectpicker's current options
            function prependOptions(options) {
                _addOptions(options.reverse(), option => selectPicker.prepend(option));
                return this;
            }

            // Append the supplied list of options to the selectpicker's current options
            function appendOptions(options) {
                _addOptions(options, option => selectPicker.append(option));
                return this;
            }

            // Add the supplied list of options according to the passed in addOption function
            function _addOptions(options, addOption) {

                var selectedOptions = [];

                options.forEach(function (option) {

                    // Get option properties 
                    var optionValue = coalesce(option.Value, option.value);
                    var optionText = coalesce(option.Text, option.text);
                    var optionSelected = coalesce(option.Selected, option.selected);

                    // add option
                    addOption($("<option></option>").val(optionValue).text(optionText));

                    // If the added option is marked to be selected, add it to the selectedOptions array
                    // We are using an array to account for multi selects
                    if (optionSelected) {
                        selectedOptions.push(optionValue);
                    }
                });

                // If any of the items that were appended were marked selected, set selected value(s) on the select picker
                if (selectedOptions.length > 0) {

                    // If this is a multi select, keep currently selected options in addition to any appended options that were marked selected
                    if (selectPicker[0].hasAttribute("multiple")) {

                        selectPicker.val(selectPicker.val().concat(selectedOptions));
                    }
                    else {
                        // If is is a single select, set the selected value to the appended option marked selected
                        selectPicker.val(selectedOptions[0]);
                    }
                }

                // Refresh the select picker's options for display
                selectPicker.selectpicker('refresh');

                // Trigger the selectpicker's loaded event immediately
                selectPicker.trigger('loaded.bs.select');

                // Size the container to fit the new width of the select
                adjustContainerWidth();
            }

            // Replace the selectpicker's options with the supplied list of options
            function replaceOptions(options, specialOptions) {

                var isMultiSelect = selectPicker[0].hasAttribute("multiple");

                // Get the previously selected value(s)
                // We need to account for the possibility of a multi select, where the selected value is an array
                // If the select picker is a single select, convert it to an array so we know we will always have an array going forward
                var selectedValues = isMultiSelect ? selectPicker.val() : [selectPicker.val()];

                // clear existing options
                selectPicker.empty();

                // add a blank option if indicated in special options 
                if (specialOptions != null) {
                    if (specialOptions.withBlank) {

                        var blankOption = $("<option></option>");

                        // if blankValue was supplied, set the value of the blank option
                        if (specialOptions.hasOwnProperty("blankValue")) {
                            blankOption.val(specialOptions.blankValue);
                        }

                        selectPicker.append(blankOption);
                    }

                    if (specialOptions.maintainSelection) {

                        options = options.map(_ =>
                            ({
                                Text: coalesce(_.Text, _.text),
                                Value: coalesce(_.Value, _.value),
                                Selected: selectedValues.indexOf(coalesce(_.Value, _.value)) > -1 || (isMultiSelect && coalesce(_.Selected, _.selected))
                            }));
                    }
                }

                // append options
                appendOptions(options);

                // Trigger the selectpicker's change event (unless special options indicate it shouldn't fire)
                if (specialOptions == null || (!specialOptions.suppressChange && !specialOptions.maintainSelection)) {
                    selectPicker.trigger('change');
                }

                return this;
            }

            // Retrieve and load selectpicker's options from supplied URL and arguments 
            function loadOptionsFrom(url, args, specialOptions, callback) {

                var isMultiSelect = selectPicker[0].hasAttribute("multiple");

                // Get the previously selected value(s)
                // We need to account for the possibility of a multi select, where the selected value is an array
                // If the select picker is a single select, convert it to an array so we know we will always have an array going forward
                var previouslySelectedValues = isMultiSelect ? selectPicker.val() : [selectPicker.val()];

                // clear any existing items
                selectPicker.empty();

                // save off this selecthelper so we can return it in the callback
                var selectHelper = this;

                // load options from URL
                $.getJSON(url, args)
                    .done(function (options) {

                        var selectedValues = [];

                        // add a blank option if indicated in special options 
                        if (specialOptions != null) {
                            if (specialOptions.withBlank) {

                                var blankOption = $("<option></option>");

                                // if blankValue was supplied, set the value of the blank option
                                if (specialOptions.hasOwnProperty("blankValue")) {
                                    blankOption.val(specialOptions.blankValue);
                                }

                                selectPicker.append(blankOption);
                            }

                            // Update selected property of options to maintain selection(s) if indicated in special options
                            if (specialOptions.maintainSelection) {
                                options = options.map(_ =>
                                ({
                                    text:  _.text,
                                    value: _.value,
                                    selected: previouslySelectedValues.indexOf(_.value) > -1 || (isMultiSelect && _.selected)
                                }));
                            }
                        }

                        var selectedValues = [];
                        options.forEach(function (option) {

                            selectPicker.append($("<option></option>").val(option.value).text(option.text));

                            // If loaded option is marked to be selected, add it to the selectedValues array
                            if (option.selected) {
                                selectedValues.push(option.value);
                            }
                        });

                        // If any of the items that were loaded were marked selected, set selected value(s) on the select picker
                        if (selectedValues.length > 0) {

                            // If this is a multi select, keep currently selected options in addition to any loaded options that were marked selected
                            if (selectPicker[0].hasAttribute("multiple")) {

                                selectPicker.val(selectedValues);
                            }
                            else {
                                // If is is a single select, set the selected value to the loaded option marked selected
                                selectPicker.val(selectedValues[0]);
                            }
                        }
                    })
                    .fail(function () {
                        // return empty select - clear blank option
                        selectPicker.empty();
                    })
                    .always(function () {
                        // Refresh the select picker's options for display
                        selectPicker.selectpicker('refresh');

                        // Trigger the selectpicker's loaded event immediately
                        selectPicker.trigger('loaded.bs.select');

                        // Size the container to fit the new width of the select
                        adjustContainerWidth();

                        // if a call back has been defined, execute it, sending this selecthelper as the 'this' argument
                        if (callback) {
                            callback.call(selectHelper);
                        }
                    });

                return this;
            }

            // Disable selectpicker options based on supplied array of values 
            function disableOptions(values) {

                var isMultiSelect = selectPicker[0].hasAttribute("multiple");

                // Get the previously selected value(s)
                // We need to account for the possibility of a multi select, where the selected value is an array
                // If the select picker is a single select, convert it to an array so we know we will always have an array going forward
                var selectedValues = isMultiSelect ? selectPicker.val() : [selectPicker.val()];

                for (let i = 0; i < values.length; i++) {
                    selectPicker.find(`option[value='${values[i]}']`).prop("disabled", "disabled");

                    // Is the disabled value selected? If so remove it from the list of selected values
                    var index = selectedValues.indexOf(values[i]);
                    if (index > -1) {
                        selectedValues.splice(index, 1);
                    }
                }

                // update the selected value(s) after disabling options
                if (isMultiSelect) {
                    selectPicker.val(selectedValues);
                }
                else {
                    if (selectedValues.length > 0) {
                        selectPicker.val(selectedValues[0]);
                    }
                    else {
                        selectPicker.val(null);
                    }
                }

                // Refresh the select picker's options for display
                selectPicker.selectpicker('refresh');

                return this;
            }

            // Enable all option in the selectpicker
            function enableAllOptions() {

                selectPicker.find('option').prop("disabled", false);

                // Refresh the select picker's options for display
                selectPicker.selectpicker('refresh');

                return this;
            }

            // Adjust the size the select picker's container to fit the new width of the select
            function adjustContainerWidth() {

                // The adjusted width needs to be +8px to account for the button overlay so that text doesn't get cut off
                selectPicker.parent().width(selectPicker.width() + 8);
            }

            // Perform object coalescing
            function coalesce(value1, value2) {
                if (value1 === null || value1 === undefined) {
                    return value2;
                }
                return value1;
            }

            var controlExports = {
                setValue: setValue,
                selectIndex: selectIndex,
                getOptions: getOptions,
                clearOptions: clearOptions,
                setDisabled: setDisabled,
                prependOptions: prependOptions,
                addEmptyOption: addEmptyOption,
                removeEmptyOption: removeEmptyOption,
                appendOptions: appendOptions,
                replaceOptions: replaceOptions,
                loadOptionsFrom: loadOptionsFrom,
                setMultiSelect: setMultiSelect,
                disableOptions,
                enableAllOptions
            };

            return controlExports;
        }

        /// the interface to the control
        var exports = {
            getHelper: getHelper
        };

        return exports;
    }
);